import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Plus, LocateFixed, Loader2, Phone, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddressPickerCheckout({ token, onAddressSelected, initialAddress, initialPhone, initialLocation }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [useNew, setUseNew] = useState(false);
  const [locating, setLocating] = useState(false);
  const [newAddr, setNewAddr] = useState({
    address: initialAddress || '',
    phone: initialPhone || '',
    lat: initialLocation?.lat || null,
    lng: initialLocation?.lng || null,
  });

  const fetchAddresses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/addresses/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const addrs = data.addresses || [];
      setAddresses(addrs);

      // Auto-select default
      const def = addrs.find(a => a.is_default);
      if (def) {
        setSelectedId(def.id);
        onAddressSelected?.({
          address: def.address_line,
          phone: def.phone,
          location: def.lat ? { lat: def.lat, lng: def.lng } : null,
          address_id: def.id,
        });
      } else if (addrs.length === 0) {
        setUseNew(true);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const selectAddress = (addr) => {
    setSelectedId(addr.id);
    setUseNew(false);
    onAddressSelected?.({
      address: addr.address_line,
      phone: addr.phone,
      location: addr.lat ? { lat: addr.lat, lng: addr.lng } : null,
      address_id: addr.id,
    });
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setNewAddr(f => ({ ...f, lat: latitude, lng: longitude }));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const data = await res.json();
          if (data.display_name) {
            setNewAddr(f => ({ ...f, address: data.display_name }));
          }
        } catch (err) { console.error(err); }
        setLocating(false);
        // Emit to parent
        onAddressSelected?.({
          address: newAddr.address || 'Current Location',
          phone: newAddr.phone,
          location: { lat: latitude, lng: longitude },
        });
      },
      () => { setLocating(false); toast.error('Location access denied'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleNewInput = (field, value) => {
    const updated = { ...newAddr, [field]: value };
    setNewAddr(updated);
    onAddressSelected?.({
      address: updated.address,
      phone: updated.phone,
      location: updated.lat ? { lat: updated.lat, lng: updated.lng } : null,
    });
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Loading addresses...</div>;

  return (
    <div className="space-y-3" data-testid="address-picker">
      {/* Saved addresses */}
      {addresses.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">Select delivery address</Label>
          {addresses.map(addr => (
            <div
              key={addr.id}
              className={`p-2.5 border rounded-lg cursor-pointer text-sm transition-colors ${selectedId === addr.id && !useNew ? 'border-teal-500 bg-teal-50' : 'hover:border-slate-300'}`}
              onClick={() => selectAddress(addr)}
              data-testid={`pick-addr-${addr.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {selectedId === addr.id && !useNew && <Check className="h-4 w-4 text-teal-600" />}
                  <span className="font-medium text-slate-800">{addr.label}</span>
                  {addr.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                </div>
                {addr.phone && <span className="text-xs text-slate-400"><Phone className="h-3 w-3 inline mr-1" />{addr.phone}</span>}
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-6">{addr.address_line}</p>
            </div>
          ))}
        </div>
      )}

      {/* New address / current location */}
      <div className={`border rounded-lg p-3 space-y-2 ${useNew ? 'border-teal-500 bg-teal-50/30' : ''}`}>
        <div className="flex items-center justify-between">
          <Button
            size="sm"
            variant={useNew ? 'default' : 'outline'}
            onClick={() => { setUseNew(true); setSelectedId(null); }}
            data-testid="use-new-address-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> New Address
          </Button>
          <Button size="sm" variant="outline" onClick={getCurrentLocation} disabled={locating}>
            {locating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <LocateFixed className="h-3 w-3 mr-1" />}
            Use Current Location
          </Button>
        </div>

        {useNew && (
          <div className="space-y-2 pt-1">
            <Input
              placeholder="Full delivery address"
              value={newAddr.address}
              onChange={e => handleNewInput('address', e.target.value)}
              data-testid="checkout-address-input"
            />
            <Input
              placeholder="Phone number"
              value={newAddr.phone}
              onChange={e => handleNewInput('phone', e.target.value)}
              data-testid="checkout-phone-input"
            />
            {newAddr.lat && (
              <p className="text-xs text-teal-600"><MapPin className="h-3 w-3 inline mr-1" />{newAddr.lat.toFixed(4)}, {newAddr.lng.toFixed(4)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
